// ENG-3676: behind `title_opens_side_panel`, a document title or patient name in the
// inbox lists opens the side panel instead of the full page. The full page stays one
// click away: the row's hover "Full view" link and the panel's "Open full view" button.
//
// The title stays a real link so Command/Ctrl-click, middle-click and copy-link still
// reach the full page. Only a plain left click is held back: the link's own onClick
// prevents its navigation, and InboxTable's row click sees the held click and opens
// the side panel.

import { hasFeatureInLocalStorage, TITLE_OPENS_SIDE_PANEL_FEATURE } from '../global/featureFlags';
import { opensMessageDrawerOnClick } from './inboxRowCapabilities';

// Marks the links whose plain click opens the side panel, so the row's click handler
// can tell them apart from every other link in the row.
export const SIDE_PANEL_LINK_ATTRIBUTE = 'data-opens-side-panel';
export const SIDE_PANEL_LINK_SELECTOR = `a[${SIDE_PANEL_LINK_ATTRIBUTE}]`;

// The Chrome extension selector picks documents by row selection, so its links keep
// their current behavior.
export const titleOpensSidePanel = (viaChromeExtension = false) => (
  !viaChromeExtension && hasFeatureInLocalStorage(TITLE_OPENS_SIDE_PANEL_FEATURE)
);

// Whether this row's title and patient-name links open the side panel. A row whose
// click opens message history (a reply row) keeps them as plain full-page links:
// marking them would let the row handler read the router Link's own prevented
// click as a held one and open the panel on top of the navigation, leaving two
// history entries (docs/solutions/runtime-errors/inbox-links-must-own-viewer-navigation.md).
export const linkOpensSidePanel = (record, viaChromeExtension = false) => (
  titleOpensSidePanel(viaChromeExtension) && !opensMessageDrawerOnClick(record)
);

const isPlainLeftClick = (event) => (
  (event.button === undefined || event.button === 0)
  && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
);

// The link's onClick. A row whose click opens message history (a reply row) keeps
// the title as a full-page link, as today, rather than opening the message drawer.
export const holdTitleClickForSidePanel = (event, record) => {
  if (!isPlainLeftClick(event) || opensMessageDrawerOnClick(record)) return;
  event.preventDefault();
};

// True for a click InboxTable should turn into a side-panel open.
export const isHeldSidePanelClick = (event) => Boolean(
  event.defaultPrevented && event.target?.closest?.(SIDE_PANEL_LINK_SELECTOR),
);

// The title or patient-name Link's onClick, or undefined when the link stays a
// plain full-page link.
export const sidePanelLinkClick = (record, viaChromeExtension = false) => (
  linkOpensSidePanel(record, viaChromeExtension)
    ? (event) => holdTitleClickForSidePanel(event, record)
    : undefined
);

// The marker attribute's value for a title or patient-name Link.
export const sidePanelLinkMarker = (record, viaChromeExtension = false) => (
  linkOpensSidePanel(record, viaChromeExtension) ? 'true' : undefined
);
